import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Edit3, Grid3X3, MapPinned, Plus, QrCode } from 'lucide-react';
import { clubsService } from '../../../services';
import './ManageClubs.css';
import QRCodeModal from './QRCodeModal';
import VenueSubnav from './VenueSubnav';
import { compareStandsByIdentity, extractTargetLabels, getStandSetupStatus, parseStandIdentity } from './standClayConfig';

const qrModalStyles = `
.qr-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.qr-modal-content {
    background-color: white;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    max-width: 90%;
    max-height: 90%;
    overflow: auto;
    position: relative;
}

.close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
}

.qr-branded-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.qr-logo {
    max-width: 200px;
    margin-bottom: 15px;
}

.stand-info {
    margin: 10px 0;
}

.qr-code-container {
    margin: 20px 0;
}

.website-url {
    margin-top: 10px;
    color: #555;
}

.qr-actions {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
}

@media (max-width: 768px) {
    .qr-actions {
        flex-direction: column;
    }

    .qr-actions button {
        margin: 5px 0 !important;
    }
}
`;

const ManageStands = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [stands, setStands] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStand, setSelectedStand] = useState(null);

    const fetchStands = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [clubData, standData] = await Promise.all([
                clubsService.getClub(clubId),
                clubsService.getClubStands(clubId)
            ]);

            setClub(clubData);
            setStands(Array.isArray(standData)
                ? standData
                : Array.isArray(clubData?.stands)
                    ? clubData.stands
                    : []);
        } catch (err) {
            console.error(err);
            setError('Failed to load stands. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        fetchStands();

        const styleElement = document.createElement('style');
        styleElement.innerHTML = qrModalStyles;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, [fetchStands]);

    const handleAddStand = () => {
        navigate(`/dashboard/club/${clubId}/stands/add`);
    };

    const handleOpenCourseBuilder = () => {
        navigate(`/dashboard/club/${clubId}/stands/course-builder`);
    };

    const handleEditStand = (standId) => {
        navigate(`/dashboard/club/${clubId}/stands/edit/${standId}`);
    };

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading venue stands...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-error" role="alert">{error}</div>
            </div>
        );
    }

    const sortedStands = [...stands].sort(compareStandsByIdentity);

    return (
        <div className="manage-clubs-page">
            <button
                className="club-dashboard-back"
                onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}
            >
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-green">
                        <Grid3X3 size={24} />
                    </div>
                    <div>
                        <h1>Manage Stands</h1>
                        <p>Configure shooting stands, pegs, and venue QR access for {club?.name || 'this venue'}.</p>
                    </div>
                </div>
                <div className="manage-header-actions">
                    <button
                        className="dashboard-card-outline-btn"
                        onClick={handleOpenCourseBuilder}
                    >
                        <MapPinned size={18} />
                        Course Builder
                    </button>
                    <button
                        className="manage-section-add-btn"
                        onClick={handleAddStand}
                    >
                        <Plus size={18} />
                        Add Stand
                    </button>
                </div>
            </div>

            <div className="stand-course-guide">
                <div className="stand-course-guide-copy">
                    <span className="stand-course-guide-eyebrow">Course setup</span>
                    <h2>Build the venue in the same order shooters see it on the ground</h2>
                    <p>Use stand numbers where your course has them, or just use layout names like Rabbits, English Skeet, or 90 Tower when that is how the ground is signed.</p>
                </div>
                <div className="stand-course-guide-stats">
                    <div className="stand-course-guide-stat">
                        <strong>{sortedStands.length}</strong>
                        <span>stands added</span>
                    </div>
                    <div className="stand-course-guide-stat">
                        <strong>{sortedStands.filter((stand) => extractTargetLabels(stand.clayConfigJson).length > 0).length}</strong>
                        <span>with clay setup</span>
                    </div>
                </div>
            </div>

            {stands.length === 0 ? (
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        <Grid3X3 size={48} />
                    </div>
                    <h3>No Stands Yet</h3>
                    <p>Add your first stand to map out the shooting layout for this venue.</p>
                </div>
            ) : (
                <div className="stands-list">
                    {sortedStands.map((stand) => {
                        const standIdentity = parseStandIdentity(stand.name);
                        const clayLabels = extractTargetLabels(stand.clayConfigJson);
                        const setupStatus = getStandSetupStatus(stand);

                        return (
                            <div key={stand.standId} className="stand-card">
                                <div className="stand-card-top">
                                    <div className="stand-card-number">
                                        <span>{standIdentity.standNumber || <Grid3X3 size={22} />}</span>
                                    </div>
                                    <div className="stand-card-content">
                                        <div className="stand-card-heading">
                                            <div>
                                                <span className="stand-card-kicker">
                                                    {standIdentity.standNumber ? `Stand ${standIdentity.standNumber}` : 'Stand'}
                                                </span>
                                                <h3>{standIdentity.standTitle || stand.name}</h3>
                                            </div>
                                            <span className="stand-card-label">Stand</span>
                                        </div>
                                        <p>{stand.description || 'Add a short presentation summary so this stand is easy to recognise from the course map.'}</p>
                                        <div className="stand-card-meta">
                                            {stand.discipline ? <span className="stand-chip">{stand.discipline}</span> : null}
                                            {stand.variant ? <span className="stand-chip">{stand.variant}</span> : null}
                                            <span className={`stand-chip ${setupStatus === 'configured' ? 'stand-chip-success' : 'stand-chip-warning'}`}>
                                                {setupStatus === 'configured' ? 'Configured' : 'Needs clay setup'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="stand-card-route">
                                    <span className="stand-card-route-label">Course view</span>
                                    <strong>{standIdentity.displayName || stand.name}</strong>
                                </div>
                                {stand.courseMapX != null && stand.courseMapY != null ? (
                                    <div className="stand-card-route">
                                        <span className="stand-card-route-label">Map coordinates</span>
                                        <strong>{`${(stand.courseMapX * 100).toFixed(1)}%, ${(stand.courseMapY * 100).toFixed(1)}%`}</strong>
                                    </div>
                                ) : null}
                                {clayLabels.length > 0 ? (
                                    <div className="stand-card-layout">
                                        <span className="stand-card-layout-title">Clay Labels</span>
                                        <div className="stand-setup-summary">
                                            {clayLabels.map((label) => (
                                                <span key={label} className="stand-setup-summary-label">{label}</span>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                                <div className="stand-card-actions">
                                    <button
                                        className="stand-action-btn"
                                        onClick={() => handleEditStand(stand.standId)}
                                    >
                                        <Edit3 size={16} />
                                        Edit
                                    </button>
                                    <button
                                        className="stand-action-btn"
                                        onClick={() => setSelectedStand(stand)}
                                    >
                                        <QrCode size={16} />
                                        View QR
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedStand ? (
                <QRCodeModal
                    stand={selectedStand}
                    onClose={() => setSelectedStand(null)}
                />
            ) : null}
        </div>
    );
};

export default ManageStands;
